/**
 * Konami easter egg — hidden Coach Mode dialog. Trigger the sequence
 * ↑↑↓↓←→←→BA anywhere in the app and this pops up: SPC-branded gym playlist,
 * private message-the-coach chip, and a fun bit of copy.
 *
 * Also exposes the live-presence chip in the corner (opt-in — only renders if
 * the backend endpoint returns > 0 online).
 */
import React, { useEffect, useRef, useState } from "react";
import { useKonami, fireConfetti } from "@/lib/quirks";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Music, MessageSquare, Trophy, Sparkles } from "lucide-react";
import api from "@/lib/api";

export default function QuirkyLayer() {
  const [open, setOpen] = useState(false);
  const [online, setOnline] = useState(0);
  // 5 taps on the SPC logo within 3 s = unlock. Discoverable secret handshake
  // that works on mobile + desktop, so every athlete eventually stumbles on it.
  const taps = useRef([]);

  const unlock = () => {
    fireConfetti({ count: 120 });
    setOpen(true);
  };

  useKonami(unlock);

  useEffect(() => {
    const onDocClick = (e) => {
      // Match any click that lands on or inside the brand logo.
      if (!e.target.closest?.('[data-testid="brand-logo"]')) return;
      const now = Date.now();
      taps.current = [...taps.current.filter((t) => now - t < 3000), now];
      if (taps.current.length >= 5) {
        taps.current = [];
        unlock();
      }
    };
    document.addEventListener("click", onDocClick, true);
    return () => document.removeEventListener("click", onDocClick, true);
  }, []);

  // Live presence — poll every 60 s, non-critical so failures are silent.
  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const { data } = await api.get("/presence/online");
        if (alive) setOnline(data?.count || 0);
      } catch { /* silent — the app doesn't need this to work */ }
    };
    load();
    const id = setInterval(load, 60_000);
    return () => { alive = false; clearInterval(id); };
  }, []);

  return (
    <>
      {online > 0 && (
        <div data-testid="presence-chip"
          className="fixed bottom-4 right-4 z-40 pointer-events-none flex items-center gap-2 border border-[#34C759]/30 bg-[#0A0A0A]/85 backdrop-blur px-3 py-1.5 text-[11px] font-mono text-zinc-200 shadow-lg"
          title="Athletes active in the last 30 minutes">
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#34C759] opacity-75" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-[#34C759]" />
          </span>
          <span><b className="text-white">{online}</b> {online === 1 ? "athlete" : "athletes"} fuelling right now</span>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="konami-dialog" className="bg-[#0A0A0A] border-[#E2E30A]/40 rounded-none text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display uppercase tracking-tight text-2xl flex items-center gap-2">
              <Sparkles className="h-6 w-6 text-[#E2E30A]" /> Coach Mode Unlocked
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2 text-sm text-zinc-200">
            <p className="text-zinc-300">Nice one — you found the Konami. Here's a little something for the athletes who read the manual.</p>
            <a href="https://open.spotify.com/playlist/37i9dQZF1DXcxvFzl58uP7" target="_blank" rel="noreferrer"
              className="flex items-center justify-between gap-3 border border-white/10 hover:border-[#E2E30A]/60 hover:bg-[#E2E30A]/[0.04] px-3 py-3 transition-colors">
              <div className="flex items-center gap-3"><Music className="h-4 w-4 text-[#E2E30A]" /><span className="uppercase tracking-wider text-xs">SPC Tempo · Spotify</span></div>
              <span className="text-[10px] text-zinc-500">Open →</span>
            </a>
            <a href="mailto:info@smartperformancecoaching.co.uk?subject=Coach Mode DM"
              className="flex items-center justify-between gap-3 border border-white/10 hover:border-[#E2E30A]/60 hover:bg-[#E2E30A]/[0.04] px-3 py-3 transition-colors">
              <div className="flex items-center gap-3"><MessageSquare className="h-4 w-4 text-[#E2E30A]" /><span className="uppercase tracking-wider text-xs">Message the coach</span></div>
              <span className="text-[10px] text-zinc-500">Private line</span>
            </a>
            <div className="flex items-center gap-3 border border-white/10 px-3 py-3">
              <Trophy className="h-4 w-4 text-[#E2E30A]" />
              <div>
                <p className="uppercase tracking-wider text-xs">Konami badge earned</p>
                <p className="text-[11px] text-zinc-400">Tell nobody. Or tell everybody — up to you.</p>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
